import { ParsedCsvRow, AnalysisParams } from '@/components/user/csv-analysis/types';

type AntiLossStreakState = 'WAITING' | 'ACTIVE' | 'PAUSED';

interface AntiLossStreakResult {
  trades: ParsedCsvRow[];
  totalSkipped: number;
  skippedInWaiting: number;
  skippedInPaused: number;
  stateTransitions: number;
}

export class AntiLossStreakService {
  /**
   * Apply Anti Loss Streak rule to trading data with mode support
   *
   * Logic:
   * - WAITING: Count consecutive losses, skip all trades until waitForLosses is reached
   * - ACTIVE: Include trades; mode determines stopping condition
   *   - consecutive_losses: count consecutive losses; enter PAUSED after m-th loss trade is included
   *   - consecutive_trades: count all trades; enter PAUSED after m-th trade is included
   * - Breakeven in ACTIVE resets the loss counter (consecutive_losses mode only)
   * - PAUSED: Skip all trades until a win; that win transitions to WAITING (included if resetOnWin=false)
   */
  static applyAntiLossStreakRule(
    trades: ParsedCsvRow[], 
    waitForLosses: number, 
    stopAfterLosses: number,
    resetOnWin: boolean = true,
    mode: 'consecutive_losses' | 'consecutive_trades' = 'consecutive_losses'
  ): AntiLossStreakResult {
    if (waitForLosses <= 0 || stopAfterLosses <= 0) {
      return {
        trades: [...trades],
        totalSkipped: 0,
        skippedInWaiting: 0,
        skippedInPaused: 0,
        stateTransitions: 0
      };
    }

    const result: ParsedCsvRow[] = [];
    let state: AntiLossStreakState = 'WAITING';
    let consecutiveLosses = 0;
    let activeLosses = 0; // Track losses specifically in ACTIVE state
    let activeTrades = 0; // Track all trades in ACTIVE state (for consecutive_trades mode)
    let skippedInWaiting = 0;
    let skippedInPaused = 0;
    let stateTransitions = 0;

    for (const trade of trades) {
      const isWin = trade.pnl > 0;
      const isLoss = trade.pnl < 0;
      
      let includeTradeInResult = false;
      let skipReason = '';

      // State machine logic
      switch (state) {
        case 'WAITING':
          if (isLoss) {
            consecutiveLosses++;
            if (consecutiveLosses >= waitForLosses) {
              // Switch to ACTIVE and INCLUDE this triggering loss trade
              state = 'ACTIVE';
              activeLosses = 1; // This trade counts as first active loss
              activeTrades = 1; // This trade counts as first active trade
              stateTransitions++;
              includeTradeInResult = true;
            } else {
              skipReason = `Waiting for ${waitForLosses} losses (${consecutiveLosses}/${waitForLosses})`;
              skippedInWaiting++;
            }
          } else if (isWin) {
            consecutiveLosses = 0; // Reset waiting counter on win
            skipReason = `Waiting for ${waitForLosses} consecutive losses`;
            skippedInWaiting++;
          } else {
            // Breakeven trade - continue waiting
            skipReason = `Waiting for ${waitForLosses} consecutive losses`;
            skippedInWaiting++;
          }
          break;

        case 'ACTIVE':
          // Only increment counters if we're not coming from WAITING (already counted above)
          if (consecutiveLosses < waitForLosses || activeTrades > 0) {
            activeTrades++;
          }
          includeTradeInResult = true; // Always include trades in ACTIVE state
          
          if (mode === 'consecutive_losses') {
            // Count consecutive losses
            if (isLoss) {
              // Only increment if we're not coming from WAITING (already counted above)
              if (consecutiveLosses < waitForLosses || activeLosses > 0) {
                activeLosses++;
              }
              if (activeLosses >= stopAfterLosses) {
                // Reached the threshold: enter PAUSED starting from the next trade
                state = 'PAUSED';
                stateTransitions++;
              }
            } else if (isWin) {
              activeLosses = 0; // Reset loss counter on win
            } else {
              // Breakeven trade - reset the active loss counter
              activeLosses = 0;
            }
          } else {
            // Count all trades
            if (activeTrades >= stopAfterLosses) {
              // Reached the threshold: enter PAUSED starting from the next trade
              state = 'PAUSED';
              stateTransitions++;
            }
          }
          break;

        case 'PAUSED':
          if (isWin) {
            if (resetOnWin) {
              state = 'WAITING';
              consecutiveLosses = 0;
              activeLosses = 0;
              activeTrades = 0;
              stateTransitions++;
              // Skip the winning trade that resets the state
              skipReason = `Win trade that resets to waiting state (skipped)`;
              skippedInPaused++;
            } else {
              state = 'ACTIVE';
              activeLosses = 0;
              activeTrades = 1; // Count this win as first active trade
              stateTransitions++;
              includeTradeInResult = true;
            }
          } else {
            skipReason = mode === 'consecutive_losses' 
              ? `Paused after ${stopAfterLosses} consecutive losses in active state`
              : `Paused after ${stopAfterLosses} trades in active state`;
            skippedInPaused++;
          }
          break;
      }

      // Add trade to result
      if (includeTradeInResult) {
        result.push({
          ...trade,
          isSkipped: false
        });
      } else {
        result.push({
          ...trade,
          isSkipped: true,
          skipReason: skipReason
        });
      }
    }

    return {
      trades: result,
      totalSkipped: skippedInWaiting + skippedInPaused,
      skippedInWaiting,
      skippedInPaused,
      stateTransitions
    };
  }

  /**
   * Apply anti loss streak rule with analysis parameters
   */
  static applyAntiLossStreakFromParams(
    trades: ParsedCsvRow[], 
    params: AnalysisParams
  ): ParsedCsvRow[] {
    if (!params.anti_loss_streak_enabled) {
      return trades;
    }

    const result = this.applyAntiLossStreakRule(
      trades,
      params.wait_for_losses,
      params.stop_after_losses,
      params.reset_on_win,
      params.anti_loss_streak_mode
    );

    return result.trades;
  }
}